import * as React from 'react';
import { Hourglass } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { Order } from '@klareospace/pharos-lib-types';
import { IconText, Input } from '@klareospace/pharos-lib-web-utils';

import type { UpdateOrderRequest } from '../api';
import { useInlineOrderFieldEdit } from '../hooks/use-inline-order-field-edit';
import type { OrderFieldEditState, OrderFieldLockReason } from '../hooks/use-order-editability';
import { EditableFieldWrapper } from './editable-field-wrapper';
import { InlineEditActions } from './inline-edit-actions';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DurationTimeInlineEditorProps {
  readonly buildPatch: (value: number) => UpdateOrderRequest;
  readonly durationTime: number | undefined;
  readonly fieldState: OrderFieldEditState;
  readonly lockReason: OrderFieldLockReason | null;
  readonly order: Order;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isValidDuration(value: number): boolean {
  return Number.isFinite(value) && value >= 0;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DurationTimeInlineEditor({
  buildPatch,
  durationTime,
  fieldState,
  lockReason,
  order,
}: DurationTimeInlineEditorProps): React.JSX.Element {
  const { t } = useTranslation('orders');
  const { draft, handleCancel, handleConfirm, isEditing, isPending, isValid, setDraft, startEdit } =
    useInlineOrderFieldEdit<number>({
      buildPatch,
      isValid: isValidDuration,
      order,
      successKey: 'drawer.edit.content.success',
      value: durationTime ?? 0,
    });

  if (isEditing) {
    return (
      <div className="mt-1 flex items-center gap-1">
        <Input
          className="h-7 flex-1 text-sm"
          disabled={isPending}
          min={0}
          onChange={(e): void => setDraft(Number(e.target.value))}
          step="0.01"
          type="number"
          value={draft}
        />
        <span className="text-xs text-muted-foreground">{t('drawer.durationUnit')}</span>
        <InlineEditActions isPending={isPending} isValid={isValid} onCancel={handleCancel} onConfirm={handleConfirm} />
      </div>
    );
  }

  return (
    <EditableFieldWrapper fieldState={fieldState} lockReason={lockReason} onEdit={startEdit}>
      {durationTime !== undefined ? (
        <IconText icon={Hourglass} size="sm">
          <span className="truncate text-foreground">{t('drawer.durationValue', { count: durationTime })}</span>
        </IconText>
      ) : (
        <span className="text-sm text-muted-foreground">{t('drawer.noDuration')}</span>
      )}
    </EditableFieldWrapper>
  );
}
